// Helpers for conditional pricing modifiers.
//
// A conditional pricing modifier group (`isConditionalPricingModifier`) references
// a base price modifier group (`isBasePriceModifier`). Each conditional option
// carries a `conditionalCharges` map keyed by the base-price option's normalized
// name key, giving a different price depending on which base option the customer
// selected.

/** Normalized key used to match a base-price option name to `conditionalCharges`. */
export const getOptionNameKey = (name) => (name ?? "").trim().toLowerCase();

/**
 * Resolve the effective charge for an option, taking conditional pricing into
 * account. For conditional groups with a resolved base-price option key, the
 * price comes from `conditionalCharges[basePriceOptionNameKey]`. When the group
 * is conditional but no base key is available yet, return 0 so extras stay hidden.
 * Otherwise use the plain `charge`.
 */
export const resolveOptionCharge = (option, group, basePriceOptionNameKey) => {
  if (group?.isConditionalPricingModifier) {
    if (!basePriceOptionNameKey) return 0;
    const conditional = option?.conditionalCharges?.[basePriceOptionNameKey];
    const value = Number(conditional);
    return Number.isFinite(value) ? value : 0;
  }
  const base = Number(option?.charge);
  return Number.isFinite(base) ? base : 0;
};

/** True when a resolved charge is a non-zero (up or down) price adjustment. */
export const resolveOptionExtraCharge = (charge) => {
  const value = Number(charge);
  return Number.isFinite(value) && value !== 0;
};

/** The base price modifier on this item, if any. */
export const getItemBasePriceModifier = (variants) => {
  if (!Array.isArray(variants)) return undefined;
  return variants.find(
    (variant) =>
      !!variant?.isBasePriceModifier && !variant?.isConditionalPricingModifier
  );
};

/**
 * Find the normalized name key of the currently selected base-price option for
 * an item, or null when there is no base-price group / no option selected.
 * Only one base price modifier is assigned per item.
 */
export const getSelectedBasePriceOptionNameKey = (variants, variantSelected) => {
  const basePriceGroup = getItemBasePriceModifier(variants);
  if (!basePriceGroup) return null;
  const selected = (variantSelected || []).find(
    (item) => item?.optionCategory === basePriceGroup.variantName
  );
  if (!selected?.name) return null;
  return getOptionNameKey(selected.name);
};

/**
 * First stable key found across conditional charge maps on this item.
 * Used when the item has conditionals but no base-price modifier assigned.
 */
export const getFirstConditionalChargeKey = (variants) => {
  const keys = new Set();
  (variants || []).forEach((variant) => {
    if (!variant?.isConditionalPricingModifier) return;
    (variant.options || []).forEach((option) => {
      Object.keys(option?.conditionalCharges || {}).forEach((rawKey) => {
        const key = getOptionNameKey(rawKey);
        if (key) keys.add(key);
      });
    });
  });
  const sorted = [...keys].sort((a, b) => a.localeCompare(b));
  return sorted[0] || null;
};

/**
 * Resolve the base-price option name key used for conditional charge lookup:
 * 1. Currently selected base-price option (when the item has one)
 * 2. Else, when there is no base-price modifier on the item:
 *    `defaultConditionalBaseOptionKey`, then first key from charge maps
 * 3. If the item has a base-price modifier but nothing is selected yet → null
 *    (conditional extras stay hidden until the customer picks a base option)
 */
export const getEffectiveBasePriceOptionNameKey = (
  variants,
  variantSelected,
  defaultConditionalBaseOptionKey
) => {
  const selected = getSelectedBasePriceOptionNameKey(variants, variantSelected);
  if (selected) return selected;

  if (getItemBasePriceModifier(variants)) {
    return null;
  }

  const defaultKey = defaultConditionalBaseOptionKey
    ? getOptionNameKey(defaultConditionalBaseOptionKey)
    : "";
  if (defaultKey) return defaultKey;

  return getFirstConditionalChargeKey(variants) || null;
};

/**
 * Format item price from a base price modifier's option charges:
 * `$13.00-$21.50`. Falls back to null when there are no usable charges.
 */
export const formatBasePriceModifierPriceRange = (modifier) => {
  if (!modifier) return null;

  const amounts = (modifier.options || [])
    .filter((option) => option?.name?.trim())
    .map((option) => Number(option.charge))
    .filter((amount) => Number.isFinite(amount));

  if (amounts.length === 0) return null;

  const min = Math.min(...amounts);
  const max = Math.max(...amounts);
  const formatAmount = (amount) => `$${Math.abs(amount).toFixed(2)}`;

  if (min === max) return formatAmount(min);
  return `${formatAmount(min)} - ${formatAmount(max)}`;
};

/**
 * Menu/modal display price: base-price option range when present, else dish price.
 */
export const formatDishItemPriceDisplay = (dishPrice, variants) => {
  const basePriceModifier = getItemBasePriceModifier(variants);
  if (basePriceModifier) {
    const range = formatBasePriceModifierPriceRange(basePriceModifier);
    if (range) return range;
  }
  return `$${Number(dishPrice ?? 0).toFixed(2)}`;
};
